<template>
    <v-card
        class="user-menu-panel"
        :min-width="fullWidth ? undefined : 260"
        :max-width="fullWidth ? undefined : 320"
        :width="fullWidth ? '100%' : undefined"
    >
        <div class="px-4 pt-4 pb-3 d-flex align-center ga-3">
            <v-avatar size="44" color="primary">
                <v-img
                    v-if="store.user?.avatarUrl"
                    :src="store.user.avatarUrl"
                    :alt="store.user.username"
                />
                <v-icon v-else>mdi-account</v-icon>
            </v-avatar>
            <div class="d-flex flex-column overflow-hidden">
                <span class="text-subtitle-2 text-truncate" data-testid="user-menu-username">
                    {{ store.user?.username }}
                </span>
                <v-chip
                    v-if="roleLabel"
                    :color="roleColor"
                    size="x-small"
                    variant="tonal"
                    class="mt-1 align-self-start"
                    data-testid="user-menu-role"
                >
                    {{ roleLabel }}
                </v-chip>
            </div>
            <UserBadges
                v-if="store.user?.id"
                :user-id="store.user.id"
                compact
                :badge-size="40"
                class="user-menu-panel__badge ml-auto"
            />
        </div>

        <v-divider />

        <v-list density="compact" nav>
            <template v-if="canCreateGame">
                <v-list-subheader>Moje igre</v-list-subheader>
                <v-list-item
                    to="/prijavi-igru"
                    prepend-icon="mdi-plus-circle-outline"
                    title="Prijavi igru"
                    data-testid="new-game"
                />
                <v-list-item
                    to="/najavi-igru"
                    prepend-icon="mdi-calendar-plus"
                    title="Najavi igru"
                    data-testid="new-game-reservation"
                />
                <v-list-item
                    to="/gm-kalendar"
                    prepend-icon="mdi-calendar-clock"
                    title="GM Najave"
                    data-testid="gm-reservations"
                />
                <v-list-item
                    to="/moje-igre"
                    prepend-icon="mdi-dice-multiple"
                    title="Uredi igre"
                    data-testid="my-games"
                />
                <v-list-item
                    to="/moji-predlosci"
                    prepend-icon="mdi-file-document-edit-outline"
                    data-testid="my-drafts"
                >
                    <v-list-item-title class="d-flex align-center justify-space-between ga-2">
                        <span>Uredi predloške</span>
                        <v-chip
                            v-if="draftsCount != null && draftsCount > 0"
                            size="x-small"
                            color="accent"
                            variant="tonal"
                            data-testid="drafts-count"
                        >
                            {{ draftsCount }}
                        </v-chip>
                    </v-list-item-title>
                </v-list-item>
                <v-divider class="my-2" />
            </template>

            <v-list-item
                to="/profil-korisnika"
                prepend-icon="mdi-account-circle-outline"
                title="Moj profil"
                data-testid="profile-link"
            />
            <v-list-item
                to="/postavke"
                prepend-icon="mdi-cog-outline"
                title="Postavke"
                data-testid="settings-link"
            />
            <v-list-item
                to="/moje-znacke"
                prepend-icon="mdi-medal-outline"
                title="Moje značke"
                data-testid="my-badges-link"
            />

            <v-divider class="my-2" />

            <v-list-item
                prepend-icon="mdi-logout"
                title="Odjava"
                data-testid="logout-button"
                @click="$emit('logout')"
            />
        </v-list>
    </v-card>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { Role } from '../../models/User';
import { useUserStore } from '../../stores/userStore';
import UserBadges from '../badges/UserBadges.vue';

defineProps<{
    canCreateGame: boolean;
    draftsCount: number | null;
    fullWidth?: boolean;
}>();

defineEmits<{
    (event: 'logout'): void;
}>();

const store = useUserStore();

const roleLabel = computed(() => {
    switch (store.role) {
        case Role.Owner:
            return 'Vlasnik';
        case Role.Admin:
            return 'Admin';
        case Role.GM:
            return 'GM';
        case Role.Player:
            return 'Igrač';
        case Role.Bot:
            return 'Bot';
        default:
            return null;
    }
});

const roleColor = computed(() => {
    switch (store.role) {
        case Role.Owner:
        case Role.Admin:
            return 'primary';
        case Role.GM:
            return 'accent';
        default:
            return 'secondary';
    }
});
</script>

<style lang="scss" scoped>
.user-menu-panel {
    :deep(.v-list-subheader) {
        font-size: 11px;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        opacity: 0.7;
        min-height: 24px;
    }
}

.user-menu-panel__badge {
    flex-shrink: 0;
}
</style>
